'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { downloadCSV, generateHTMLReport, buildBookingReport, buildLeadReport, ReportData } from '@/lib/emailReportExport';

interface BackupJob {
  id: string;
  name: string;
  dataType: 'bookings' | 'leads' | 'analytics' | 'all';
  format: 'csv' | 'html' | 'json';
  schedule: 'manual' | 'daily' | 'weekly' | 'monthly';
  lastRun: string | null;
  nextRun: string | null;
  status: 'idle' | 'running' | 'completed' | 'failed';
  fileSize: string;
  enabled: boolean;
}

interface BackupHistory {
  id: string;
  jobName: string;
  dataType: string;
  format: string;
  timestamp: string;
  fileSize: string;
  status: 'success' | 'failed';
  recordCount: number;
}

const INITIAL_JOBS: BackupJob[] = [
  { id: 'j1', name: 'Daily Bookings Export', dataType: 'bookings', format: 'csv', schedule: 'daily', lastRun: new Date(Date.now() - 86400000).toISOString(), nextRun: new Date(Date.now() + 3600000 * 8).toISOString(), status: 'idle', fileSize: '142 KB', enabled: true },
  { id: 'j2', name: 'Weekly Lead Report', dataType: 'leads', format: 'csv', schedule: 'weekly', lastRun: new Date(Date.now() - 7 * 86400000).toISOString(), nextRun: new Date(Date.now() + 86400000 * 3).toISOString(), status: 'idle', fileSize: '89 KB', enabled: true },
  { id: 'j3', name: 'Monthly Analytics Snapshot', dataType: 'analytics', format: 'html', schedule: 'monthly', lastRun: new Date(Date.now() - 30 * 86400000).toISOString(), nextRun: new Date(Date.now() + 86400000 * 12).toISOString(), status: 'idle', fileSize: '256 KB', enabled: true },
  { id: 'j4', name: 'Full Data Backup', dataType: 'all', format: 'json', schedule: 'weekly', lastRun: new Date(Date.now() - 3 * 86400000).toISOString(), nextRun: new Date(Date.now() + 86400000 * 4).toISOString(), status: 'idle', fileSize: '1.2 MB', enabled: false },
];

const INITIAL_HISTORY: BackupHistory[] = [
  { id: 'h1', jobName: 'Daily Bookings Export', dataType: 'Bookings', format: 'CSV', timestamp: new Date(Date.now() - 86400000).toISOString(), fileSize: '142 KB', status: 'success', recordCount: 247 },
  { id: 'h2', jobName: 'Weekly Lead Report', dataType: 'Leads', format: 'CSV', timestamp: new Date(Date.now() - 7 * 86400000).toISOString(), fileSize: '89 KB', status: 'success', recordCount: 523 },
  { id: 'h3', jobName: 'Monthly Analytics Snapshot', dataType: 'Analytics', format: 'HTML', timestamp: new Date(Date.now() - 30 * 86400000).toISOString(), fileSize: '256 KB', status: 'success', recordCount: 1840 },
  { id: 'h4', jobName: 'Full Data Backup', dataType: 'All', format: 'JSON', timestamp: new Date(Date.now() - 3 * 86400000).toISOString(), fileSize: '1.2 MB', status: 'success', recordCount: 3210 },
  { id: 'h5', jobName: 'Daily Bookings Export', dataType: 'Bookings', format: 'CSV', timestamp: new Date(Date.now() - 2 * 86400000).toISOString(), fileSize: '138 KB', status: 'failed', recordCount: 0 },
];

const dataTypeConfig = {
  bookings: { label: 'Bookings', icon: 'TicketIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
  leads: { label: 'Leads', icon: 'UserGroupIcon', color: 'text-purple-600', bg: 'bg-purple-50' },
  analytics: { label: 'Analytics', icon: 'ChartBarIcon', color: 'text-green-600', bg: 'bg-green-50' },
  all: { label: 'All Data', icon: 'CircleStackIcon', color: 'text-orange-600', bg: 'bg-orange-50' },
};

const scheduleConfig = {
  manual: { label: 'Manual', color: 'bg-gray-100 text-gray-700' },
  daily: { label: 'Daily', color: 'bg-blue-100 text-blue-700' },
  weekly: { label: 'Weekly', color: 'bg-purple-100 text-purple-700' },
  monthly: { label: 'Monthly', color: 'bg-green-100 text-green-700' },
};

const buildAnalyticsReport = (): ReportData => {
  const now = new Date();
  return {
    title: 'Analytics Data Export',
    period: `${new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('en-GB')} - ${now.toLocaleDateString('en-GB')}`,
    generatedAt: now.toLocaleString('en-GB'),
    sections: [
      {
        heading: 'Traffic Summary',
        headers: ['Metric', 'Value', 'Change'],
        rows: [
          { Metric: 'Total Sessions', Value: '18,420', Change: '+24%' },
          { Metric: 'Unique Visitors', Value: '12,840', Change: '+18%' },
          { Metric: 'Page Views', Value: '54,320', Change: '+31%' },
          { Metric: 'Bounce Rate', Value: '38.2%', Change: '-4.1%' },
          { Metric: 'Avg Session Duration', Value: '4m 32s', Change: '+12%' },
        ],
      },
      {
        heading: 'Conversion Metrics',
        headers: ['Funnel Stage', 'Users', 'Conversion Rate'],
        rows: [
          { 'Funnel Stage': 'Landing Page', Users: 18420, 'Conversion Rate': '100%' },
          { 'Funnel Stage': 'Search Initiated', Users: 12840, 'Conversion Rate': '69.7%' },
          { 'Funnel Stage': 'Results Viewed', Users: 9240, 'Conversion Rate': '50.2%' },
          { 'Funnel Stage': 'Booking Started', Users: 3680, 'Conversion Rate': '20.0%' },
          { 'Funnel Stage': 'Booking Completed', Users: 1842, 'Conversion Rate': '10.0%' },
        ],
      },
    ],
  };
};

const buildAllDataReport = (): ReportData => {
  const now = new Date();
  const booking = buildBookingReport();
  const lead = buildLeadReport();
  const analytics = buildAnalyticsReport();
  return {
    title: 'Full Platform Data Export',
    period: `${new Date(now.getFullYear(), now.getMonth(), 1).toLocaleDateString('en-GB')} - ${now.toLocaleDateString('en-GB')}`,
    generatedAt: now.toLocaleString('en-GB'),
    sections: [...booking.sections, ...lead.sections, ...analytics.sections],
  };
};

const DataBackupsInteractive = () => {
  const [jobs, setJobs] = useState<BackupJob[]>(INITIAL_JOBS);
  const [history, setHistory] = useState<BackupHistory[]>(INITIAL_HISTORY);
  const [activeTab, setActiveTab] = useState<'jobs' | 'history' | 'quick-export'>('jobs');
  const [runningJobId, setRunningJobId] = useState<string | null>(null);
  const [quickFormat, setQuickFormat] = useState<'csv' | 'html'>('csv');
  const [quickDataType, setQuickDataType] = useState<'bookings' | 'leads' | 'analytics' | 'all'>('bookings');
  const [exportSuccess, setExportSuccess] = useState<string | null>(null);

  const totalBackups = history.filter(h => h.status === 'success').length;
  const totalRecords = history.filter(h => h.status === 'success').reduce((sum, h) => sum + h.recordCount, 0);
  const enabledJobs = jobs.filter(j => j.enabled).length;
  const lastBackup = history.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];

  const formatDate = (iso: string | null) => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatNextRun = (iso: string | null) => {
    if (!iso) return 'Not scheduled';
    const d = new Date(iso);
    const diff = d.getTime() - Date.now();
    if (diff < 0) return 'Overdue';
    if (diff < 3600000) return `In ${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `In ${Math.floor(diff / 3600000)}h`;
    return `In ${Math.floor(diff / 86400000)}d`;
  };

  const runJob = (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;
    setRunningJobId(jobId);
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: 'running' } : j));

    setTimeout(() => {
      let reportData: ReportData;
      if (job.dataType === 'bookings') reportData = buildBookingReport();
      else if (job.dataType === 'leads') reportData = buildLeadReport();
      else if (job.dataType === 'analytics') reportData = buildAnalyticsReport();
      else reportData = buildAllDataReport();

      if (job.format === 'csv') {
        downloadCSV(reportData, `${job.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
      } else if (job.format === 'html') {
        const html = generateHTMLReport(reportData);
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${job.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const jsonData = JSON.stringify({ report: reportData, exportedAt: new Date().toISOString() }, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${job.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }

      const newHistory: BackupHistory = {
        id: `h${Date.now()}`,
        jobName: job.name,
        dataType: dataTypeConfig[job.dataType].label,
        format: job.format.toUpperCase(),
        timestamp: new Date().toISOString(),
        fileSize: job.fileSize,
        status: 'success',
        recordCount: Math.floor(Math.random() * 500) + 100,
      };

      setHistory(prev => [newHistory, ...prev]);
      setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: 'completed', lastRun: new Date().toISOString() } : j));
      setRunningJobId(null);

      setTimeout(() => {
        setJobs(prev => prev.map(j => j.id === jobId ? { ...j, status: 'idle' } : j));
      }, 3000);
    }, 2000);
  };

  const handleQuickExport = () => {
    let reportData: ReportData;
    if (quickDataType === 'bookings') reportData = buildBookingReport();
    else if (quickDataType === 'leads') reportData = buildLeadReport();
    else if (quickDataType === 'analytics') reportData = buildAnalyticsReport();
    else reportData = buildAllDataReport();

    if (quickFormat === 'csv') {
      downloadCSV(reportData);
    } else {
      const html = generateHTMLReport(reportData);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportData.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
      a.click();
      URL.revokeObjectURL(url);
    }
    setExportSuccess(`${dataTypeConfig[quickDataType].label} exported as ${quickFormat.toUpperCase()}`);
    setTimeout(() => setExportSuccess(null), 4000);
  };

  const toggleJob = (id: string) => {
    setJobs(prev => prev.map(j => j.id === id ? { ...j, enabled: !j.enabled } : j));
  };

  return (
    <div className="min-h-screen bg-surface pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <Icon name="CircleStackIcon" size={20} className="text-white" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground">Data Backups</h1>
          </div>
          <p className="text-muted-foreground">Automated scheduled exports for leads, bookings, and analytics data</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Total Backups</span>
              <Icon name="CheckCircleIcon" size={16} className="text-green-500" />
            </div>
            <div className="text-3xl font-black text-foreground">{totalBackups}</div>
            <div className="text-xs text-muted-foreground mt-1">Successful exports</div>
          </div>
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Records Backed Up</span>
              <Icon name="DocumentDuplicateIcon" size={16} className="text-blue-500" />
            </div>
            <div className="text-3xl font-black text-foreground">{totalRecords.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground mt-1">Total records</div>
          </div>
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Active Jobs</span>
              <Icon name="ClockIcon" size={16} className="text-purple-500" />
            </div>
            <div className="text-3xl font-black text-foreground">{enabledJobs}</div>
            <div className="text-xs text-muted-foreground mt-1">of {jobs.length} configured</div>
          </div>
          <div className="bg-white rounded-xl border border-border p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Last Backup</span>
              <Icon name="CalendarIcon" size={16} className="text-orange-500" />
            </div>
            <div className="text-lg font-black text-foreground">{lastBackup ? formatDate(lastBackup.timestamp) : 'Never'}</div>
            <div className="text-xs text-muted-foreground mt-1">{lastBackup?.jobName || '—'}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl border border-border shadow-sm">
          <div className="flex border-b border-border overflow-x-auto">
            {(['jobs', 'history', 'quick-export'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-2 px-5 py-4 font-bold text-sm transition-all whitespace-nowrap ${
                  activeTab === tab ? 'text-primary border-b-2 border-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground hover:bg-surface'
                }`}
              >
                <Icon name={tab === 'jobs' ? 'CalendarDaysIcon' : tab === 'history' ? 'ClockIcon' : 'ArrowDownTrayIcon'} size={16} />
                {tab === 'jobs' ? 'Scheduled Jobs' : tab === 'history' ? 'Backup History' : 'Quick Export'}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Jobs Tab */}
            {activeTab === 'jobs' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold text-foreground">Scheduled Backup Jobs</h3>
                  <span className="text-sm text-muted-foreground">{enabledJobs} active</span>
                </div>
                <div className="space-y-4">
                  {jobs.map(job => {
                    const dtCfg = dataTypeConfig[job.dataType];
                    const schCfg = scheduleConfig[job.schedule];
                    const isRunning = runningJobId === job.id;
                    return (
                      <div key={job.id} className={`rounded-xl border p-5 transition-all ${
                        !job.enabled ? 'bg-surface border-border opacity-60' : 'bg-white border-border hover:border-primary/30'
                      }`}>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${dtCfg.bg}`}>
                              <Icon name={dtCfg.icon as any} size={20} className={dtCfg.color} />
                            </div>
                            <div>
                              <div className="font-bold text-foreground">{job.name}</div>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${schCfg.color}`}>{schCfg.label}</span>
                                <span className="text-xs text-muted-foreground">{job.format.toUpperCase()}</span>
                                <span className="text-xs text-muted-foreground">~{job.fileSize}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right hidden sm:block">
                              <div className="text-xs text-muted-foreground">Last: <span className="font-medium text-foreground">{formatDate(job.lastRun)}</span></div>
                              <div className="text-xs text-muted-foreground">Next: <span className="font-medium text-foreground">{job.enabled ? formatNextRun(job.nextRun) : 'Disabled'}</span></div>
                            </div>
                            <button
                              onClick={() => toggleJob(job.id)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${
                                job.enabled ? 'bg-primary' : 'bg-gray-300'
                              }`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                job.enabled ? 'translate-x-6' : 'translate-x-1'
                              }`} />
                            </button>
                            <button
                              onClick={() => runJob(job.id)}
                              disabled={isRunning || !!runningJobId}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                                job.status === 'completed' ? 'bg-green-100 text-green-700 border border-green-200' :
                                isRunning ? 'bg-primary/20 text-primary border border-primary/30': 'bg-primary text-white hover:bg-primary/90'
                              } disabled:opacity-50`}
                            >
                              <Icon name={isRunning ? 'ArrowPathIcon' : job.status === 'completed' ? 'CheckIcon' : 'PlayIcon'} size={14} className={isRunning ? 'animate-spin' : ''} />
                              {isRunning ? 'Running...' : job.status === 'completed' ? 'Done' : 'Run Now'}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold text-foreground">Backup History</h3>
                  <span className="text-sm text-muted-foreground">{history.length} records</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-surface">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Job Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Data Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Format</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Records</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Size</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {history.map(h => (
                        <tr key={h.id} className="hover:bg-surface/50 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground">{h.jobName}</td>
                          <td className="px-4 py-3 text-muted-foreground">{h.dataType}</td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-surface px-2 py-1 rounded font-mono font-bold">{h.format}</span>
                          </td>
                          <td className="px-4 py-3 font-bold text-foreground">{h.status === 'success' ? h.recordCount.toLocaleString() : '—'}</td>
                          <td className="px-4 py-3 text-muted-foreground">{h.fileSize}</td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(h.timestamp)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                              h.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {h.status === 'success' ? 'Success' : 'Failed'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Quick Export Tab */}
            {activeTab === 'quick-export' && (
              <div className="space-y-6">
                <h3 className="font-heading text-lg font-bold text-foreground">Quick Export</h3>
                {exportSuccess && (
                  <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
                    <Icon name="CheckCircleIcon" size={20} className="text-green-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-green-700">{exportSuccess} — download started!</span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-3">Data Type</label>
                    <div className="grid grid-cols-2 gap-3">
                      {(Object.entries(dataTypeConfig) as [typeof quickDataType, typeof dataTypeConfig[typeof quickDataType]][]).map(([key, cfg]) => (
                        <button
                          key={key}
                          onClick={() => setQuickDataType(key)}
                          className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                            quickDataType === key ? 'border-primary bg-primary/5' : 'border-border bg-white hover:border-primary/40'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cfg.bg}`}>
                            <Icon name={cfg.icon as any} size={16} className={cfg.color} />
                          </div>
                          <span className="font-bold text-sm text-foreground">{cfg.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-foreground mb-3">Export Format</label>
                    <div className="space-y-3">
                      {(['csv', 'html'] as const).map(fmt => (
                        <button
                          key={fmt}
                          onClick={() => setQuickFormat(fmt)}
                          className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                            quickFormat === fmt ? 'border-primary bg-primary/5' : 'border-border bg-white hover:border-primary/40'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            fmt === 'csv' ? 'bg-green-50' : 'bg-orange-50'
                          }`}>
                            <Icon name={fmt === 'csv' ? 'TableCellsIcon' : 'DocumentTextIcon'} size={20} className={fmt === 'csv' ? 'text-green-600' : 'text-orange-600'} />
                          </div>
                          <div className="text-left">
                            <div className="font-bold text-foreground">{fmt.toUpperCase()}</div>
                            <div className="text-xs text-muted-foreground">{fmt === 'csv' ? 'Spreadsheet-compatible, lightweight' : 'Formatted HTML report, printable'}</div>
                          </div>
                          {quickFormat === fmt && <Icon name="CheckCircleIcon" size={20} className="text-primary ml-auto" />}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleQuickExport}
                  className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white font-heading font-black rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                >
                  <Icon name="ArrowDownTrayIcon" size={20} />
                  Export {dataTypeConfig[quickDataType].label} as {quickFormat.toUpperCase()}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataBackupsInteractive;
